// Müşteriler sayfası - Müşteri listesi ve yönetimi
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FiPlus, 
  FiSearch, 
  FiEdit, 
  FiTrash2, 
  FiKey,
  FiDownload,
  FiUsers,
  FiX
} from 'react-icons/fi';
import { IoFilterCircleOutline } from 'react-icons/io5';
import { CgMenuGridO } from 'react-icons/cg';
import { customerService } from '../../services/customerService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import CustomerModal from '../../components/Customers/CustomerModal';
import DeleteConfirmModal from '../../components/Common/DeleteConfirmModal';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const Customers = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    manager: '',
    status: '',
    date: ''
  });
  const [tempFilters, setTempFilters] = useState({
    name: '',
    manager: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilters, setShowFilters] = useState({
    name: false,
    manager: false,
    status: false,
    date: false
  });

  // Müşteri listesini al
  const { data: customersData, isLoading } = useQuery(
    ['customers', currentPage, searchTerm, filters, itemsPerPage],
    () => customerService.getCustomers({
        page: currentPage,
        search: searchTerm,
        status: filters.status,
        manager: filters.manager,
        name: filters.name,
        date: filters.date,
        limit: itemsPerPage
      }),
    { 
      keepPreviousData: false,
      onSuccess: (data) => {
      },
      onError: (error) => {
      }
    }
  );

  // Müşteri silme mutation'ı
  const deleteCustomerMutation = useMutation(
    customerService.deleteCustomer,
    {
      onSuccess: (data) => {
        // Tüm müşteri query'lerini temizle ve yenile
        queryClient.invalidateQueries(['customers']);
        queryClient.removeQueries(['customers']);
        toast.success('Müşteri başarıyla silindi');
        setShowDeleteModal(false);
        setSelectedCustomer(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Müşteri silinemedi');
      }
    }
  );

  // Modal açma fonksiyonları
  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setShowModal(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  const handleDeleteCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowDeleteModal(true);
  };

  // Silme onayı
  const confirmDelete = () => {
    if (selectedCustomer) {
      deleteCustomerMutation.mutate(selectedCustomer.id);
    }
  };

  // Geçici arama terimi değişikliği (sadece input'u günceller)
  const handleTempSearchChange = (e) => {
    setTempSearchTerm(e.target.value);
  };

  // Enter tuşuna basınca aramayı uygula
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchTerm(tempSearchTerm);
      setCurrentPage(1);
    }
  };

  const handleFilterChange = (type, value) => {
    setFilters(prev => ({ ...prev, [type]: value }));
    setCurrentPage(1);
  };

  // Geçici filtre değişikliği (sadece input'u günceller)
  const handleTempFilterChange = (type, value) => {
    setTempFilters(prev => ({ ...prev, [type]: value }));
  };

  // Enter tuşuna basınca filtreyi uygula
  const handleFilterKeyDown = (type, e) => {
    if (e.key === 'Enter') {
      setFilters(prev => ({ ...prev, [type]: tempFilters[type] }));
      setCurrentPage(1);
      // Enter'a basınca filtre inputunu kapat
      setShowFilters(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleFilter = (key) => {
    setShowFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleActionMenu = (id, event) => {
    if (showActionMenu === id) {
      setShowActionMenu(null);
    } else {
      setShowActionMenu(id);
      setTimeout(() => {
        const button = event.target.closest('button');
        if (button) {
          const rect = button.getBoundingClientRect();
          const menu = document.querySelector('.action-menu-dropdown');
          if (menu) {
            menu.style.left = `${rect.right - 192}px`;
            menu.style.top = `${rect.bottom + 4}px`;
          }
        }
      }, 0);
    }
  };

  // Durum metnini döndüren fonksiyon
  const getStatusText = (status) => {
    if (status === '1') return 'Aktif';
    if (status === '0') return 'Pasif';
    return status;
  };

  // Excel export fonksiyonu
  const handleExportExcel = () => {
    
    // Veri yapısını kontrol et
    let dataToExport = customersData;
    if (customersData && typeof customersData === 'object' && !Array.isArray(customersData)) {
      // Eğer object ise, data property'sini kontrol et
      if (customersData.data && Array.isArray(customersData.data)) {
        dataToExport = customersData.data;
      } else if (customersData.customers && Array.isArray(customersData.customers)) {
        dataToExport = customersData.customers;
      }
    }
    
    
    if (!dataToExport || !Array.isArray(dataToExport) || dataToExport.length === 0) {
      toast.error('Export edilecek müşteri bulunamadı');
      return;
    }

    const exportData = dataToExport.map(customer => ({
      'Müşteri Adı': customer.customer_name,
      'Adres': customer.customer_address,
      'Telefon': customer.customer_phone,
      'Account Manager': customer.account_manager_name || '-',
      'Durum': getStatusText(customer.cutomer_status),
      'Oluşturan': customer.created_by_name || '-',
      'Oluşturulma Tarihi': new Date(customer.created_at).toLocaleDateString('tr-TR')
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Müşteriler');

    const fileName = `musteriler_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    toast.success('Excel dosyası başarıyla indirildi');
  };

  // Aktif filtre kontrolü
  const hasActiveFilters = () => {
    return searchTerm || filters.name || filters.manager || filters.status || filters.date;
  };

  // Tüm filtreleri temizle
  const clearAllFilters = () => {
    setSearchTerm('');
    setTempSearchTerm('');
    setFilters({ name: '', manager: '', status: '', date: '' });
    setTempFilters({ name: '', manager: '' });
    setShowFilters({ name: false, manager: false, status: false, date: false });
    setCurrentPage(1);
  };

  // URL parametrelerini okuyup filtreleme yap
  useEffect(() => {
    const status = searchParams.get('status');
    const manager = searchParams.get('manager');
    
    if (status) {
      if (status === 'active') {
        setFilters(prev => ({ ...prev, status: '1' }));
      } else if (status === 'inactive') {
        setFilters(prev => ({ ...prev, status: '0' }));
      }
    }
    
    if (manager) {
      setFilters(prev => ({ ...prev, manager: manager }));
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionMenu && !event.target.closest('.action-menu')) {
        setShowActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionMenu]);

  // Filtreler değiştiğinde tempFilters'ı senkronize et
  useEffect(() => {
    setTempFilters({
      name: filters.name,
      manager: filters.manager
    });
  }, [filters.name, filters.manager]);

  // searchTerm değiştiğinde tempSearchTerm'i senkronize et
  useEffect(() => {
    setTempSearchTerm(searchTerm);
  }, [searchTerm]);

  // Tablo dışına tıklayınca filtreleri kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Eğer tıklanan element tablo veya filtre içinde değilse filtreleri kapat
      const tableElement = document.querySelector('.customers-table');
      if (tableElement && !tableElement.contains(event.target)) {
        setShowFilters({ name: false, manager: false, status: false, date: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sayfa değiştirme
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const customers = customersData?.customers || [];
  const filteredAndSorted = useMemo(() => {
    let list = [...customers];
    // Backend zaten filtreleme yapıyor, sadece sıralama yapalım
    if (sortConfig.key) {
      list.sort((a, b) => {
        const av = String(a[sortConfig.key] ?? '');
        const bv = String(b[sortConfig.key] ?? '');
        if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [customers, sortConfig]);
  const pagination = customersData?.pagination || {};

  if (isLoading && !customersData) {
    return <LoadingSpinner text="Müşteriler yükleniyor..." fullScreen={false} />;
  }

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı ve aksiyonlar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <FiUsers size={24} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Müşteriler
          </h1>
        </div>
        
        {/* Guest kullanıcılar için yeni müşteri butonunu gizle */}
        {currentUser?.user_type !== 'guest' && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={handleAddCustomer}
              className="btn-primary flex items-center"
            >
              <FiPlus className="mr-2" size={16} />
              Yeni Müşteri
            </button>
          </div>
        )}
      </div>

      {/* Arama ve Export */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Müşteri ara... (Enter ile ara)"
              value={tempSearchTerm}
              onChange={handleTempSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="input-field pl-10"
            />
          </div>
          <button 
            onClick={handleExportExcel}
            className="btn-secondary flex items-center justify-center px-4 py-2"
          >
            <FiDownload className="mr-2" size={16} />
            Excel
          </button>
        </div>
      </div>

      {/* Filtre Uyarı Mesajı */}
      {hasActiveFilters() && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Filtre uygulanmıştır
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  {searchTerm && `Arama: "${searchTerm}"`}
                  {filters.name && ` • Müşteri Adı: "${filters.name}"`}
                  {filters.manager && ` • Account Manager: "${filters.manager}"`}
                  {filters.status && ` • Durum: "${getStatusText(filters.status)}"`}
                  {filters.date && ` • Tarih: "${filters.date}"`}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiX className="mr-1" size={14} />
                Tüm Filtreleri Temizle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Müşteri listesi - Mobil 2 sütun grid (sm altında) */}
      <div className="card p-4 sm:p-6 sm:hidden">
        {/* Açıklama: Mobilde tablo yerine 2 sütun kart düzeni gösterilir */}
        <div className="grid grid-cols-2 gap-3">
          {filteredAndSorted.length > 0 ? (
            filteredAndSorted.map((customer) => (
              <div
                key={customer.id}
                onClick={()=> window.location.href = `/customers/${customer.customer_uuid}`}
                className="cursor-pointer rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 hover:shadow-sm transition-shadow"
              >
                <div className="text-xs text-gray-500 dark:text-gray-400">Müşteri</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{customer.customer_name}</div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate mr-2">{customer.account_manager_name || 'Temsilci yok'}</span>
                  <span className={`${customer.cutomer_status ? 'status-active' : 'status-inactive'} text-[10px] px-2 py-0.5`}>{customer.cutomer_status ? 'Aktif' : 'Pasif'}</span>
                </div>
                <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                  {new Date(customer.created_at).toLocaleDateString('tr-TR')}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Henüz müşteri bulunmuyor</div>
          )}
        </div>
        {/* Mobil sayfalama: Önceki/Sonraki ve mevcut sayfa bilgisi */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handlePageChange(Math.max(1, Number(currentPage) - 1))}
                disabled={Number(currentPage) <= 1}
                className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                  Number(currentPage) <= 1
                    ? 'text-gray-400 border-gray-200 dark:text-gray-600 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-brand-primary hover:text-white'
                }`}
              >
                Önceki
              </button>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Sayfa {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(Number(pagination.totalPages) || 1, Number(currentPage) + 1))}
                disabled={Number(currentPage) >= Number(pagination.totalPages) }
                className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                  Number(currentPage) >= Number(pagination.totalPages)
                    ? 'text-gray-400 border-gray-200 dark:text-gray-600 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-brand-primary hover:text-white'
                }`}
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Müşteri tablosu - Masaüstü (sm ve üzeri) */}
      <div className="card hidden sm:block">
        <div className="table-container">
          <table className="table customers-table">
            <thead>
              <tr>
                <th className="relative">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('customer_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      MÜŞTERİ ADI
                      {sortConfig.key === 'customer_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('name')} className="text-gray-400 hover:text-gray-600">
                      <IoFilterCircleOutline size={16} />
                    </button>
                  </div>
                  {showFilters.name && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="text" 
                        placeholder="Filtrele... (Enter ile uygula)" 
                        value={tempFilters.name} 
                        onChange={(e) => handleTempFilterChange('name', e.target.value)} 
                        onKeyDown={(e) => handleFilterKeyDown('name', e)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="relative">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('customer_account_manager')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      MÜŞTERİ TEMSLİCİSİ
                      {sortConfig.key === 'customer_account_manager' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('manager')} className="text-gray-400 hover:text-gray-600">
                      <IoFilterCircleOutline size={16} />
                    </button>
                  </div>
                  {showFilters.manager && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="text" 
                        placeholder="Filtrele... (Enter ile uygula)" 
                        value={tempFilters.manager} 
                        onChange={(e) => handleTempFilterChange('manager', e.target.value)} 
                        onKeyDown={(e) => handleFilterKeyDown('manager', e)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="relative">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('created_at')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      OLUŞTURULMA TARİHİ
                      {sortConfig.key === 'created_at' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('date')} className="text-gray-400 hover:text-gray-600">
                      <IoFilterCircleOutline size={16} />
                    </button>
                  </div>
                  {showFilters.date && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input type="date" value={filters.date} onChange={(e)=>handleFilterChange('date', e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded" />
                    </div>
                  )}
                </th>
                <th className="relative w-32">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('cutomer_status')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      DURUM
                      {sortConfig.key === 'cutomer_status' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('status')} className="text-gray-400 hover:text-gray-600">
                      <IoFilterCircleOutline size={16} />
                    </button>
                  </div>
                  {showFilters.status && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <select value={filters.status} onChange={(e)=>handleFilterChange('status', e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded">
                        <option value="">Tümü</option>
                        <option value="1">Aktif</option>
                        <option value="0">Pasif</option>
                      </select>
                    </div>
                  )}
                </th>
                <th className="text-center w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.length > 0 ? (
                filteredAndSorted.map((customer) => (
                  <tr key={customer.id} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700" onClick={()=> window.location.href = `/customers/${customer.customer_uuid}`}>
                    <td>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {customer.customer_name}
                        </div>
                      
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {customer.account_manager_name || 'Belirtilmemiş'}
                    </td>
                    <td className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(customer.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="w-32 text-left">
                      <span className={customer.cutomer_status ? 'status-active' : 'status-inactive'}>
                        {customer.cutomer_status ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="text-center w-20" onClick={(e)=> e.stopPropagation()}>
                      <div className="relative action-menu">
                        {/* Guest kullanıcılar hiçbir müşteriyi düzenleyemez */}
                        {/* User kullanıcılar sadece kendi müşterilerini düzenleyebilir */}
                        {currentUser?.user_type !== 'guest' && 
                         !(currentUser?.user_type === 'user' && customer.customer_account_manager !== currentUser?.id) && (
                          <button
                            onClick={(e)=> toggleActionMenu(customer.id, e)}
                            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 p-2 -m-2"
                            title="İşlemler"
                          >
                            <CgMenuGridO size={18} />
                          </button>
                        )}
                        {showActionMenu === customer.id && (
                          <div className="action-menu-dropdown fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl z-[9999] min-w-48">
                            <div className="py-1">
                              <button onClick={(e)=>{e.stopPropagation(); handleEditCustomer(customer); setShowActionMenu(null);}} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <FiEdit className="mr-3" size={16} /> Düzenle
                              </button>
                              <button onClick={(e)=>{e.stopPropagation(); handleDeleteCustomer(customer); setShowActionMenu(null);}} className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <FiTrash2 className="mr-3" size={16} /> Sil
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <FiUsers size={48} className="text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400">Henüz müşteri bulunmuyor</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sayfalama */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">records</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                {pagination.totalItems} kayıt bulundu
              </div>
            </div>
            {pagination.totalPages > 1 && (
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sayfa {pagination.currentPage} / {pagination.totalPages}
                </span>
                <div className="flex space-x-1">
                  {(() => {
                    const total = Number(pagination.totalPages) || 1;
                    const current = Number(currentPage) || 1;
                    const pagesSet = new Set([1, total]);
                    for (let p = current - 1; p <= current + 1; p++) {
                      if (p > 1 && p < total) pagesSet.add(p);
                    }
                    const pages = Array.from(pagesSet).sort((a, b) => a - b);

                    const nodes = [];
                    for (let i = 0; i < pages.length; i++) {
                      const p = pages[i];
                      const prev = pages[i - 1];
                      if (i > 0 && p - prev > 1) {
                        nodes.push(
                          <span key={`ellipsis-${prev}-${p}`} className="px-2 py-2 text-sm text-gray-500 font-medium">...</span>
                        );
                      }
                      nodes.push(
                        <button
                          key={`page-${p}`}
                          onClick={() => handlePageChange(p)}
                          className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                            currentPage === p
                              ? 'bg-brand-primary text-white shadow-lg'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    }
                    return nodes;
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Müşteri ekleme/düzenleme modal'ı */}
      {showModal && (
        <CustomerModal
          customer={editingCustomer}
          onClose={() => {
            setShowModal(false);
            setEditingCustomer(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingCustomer(null);
            queryClient.invalidateQueries('customers');
          }}
        />
      )}

      {/* Silme onay modal'ı */}
      {showDeleteModal && selectedCustomer && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          title="Müşteriyi Sil"
          message={`"${selectedCustomer.customer_name}" müşterisini silmek istediğinizden emin misiniz?`}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedCustomer(null);
          }}
          isLoading={deleteCustomerMutation.isLoading}
          requireCheckbox={currentUser?.user_type === 'admin' && selectedCustomer?.account_manager_user_type === 'manager'}
          checkboxLabel="Bu müşteri Manager rolünde oluşturulmuş bir kullanıcıya aittir, silmeyi onaylıyorum"
        />
      )}
    </div>
  );
};

export default Customers;
